import Image from 'next/image'
import {FC} from 'react'

import {DomainSettings} from '@/components/footer/DomainSettings'
import {useFooterContent} from '@/content/footer'
import {TrustpilotWidgetVariant, useTrustpilotContent} from '@/content/trustpilot'
import {Link, Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {TABLET_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Text, TextVariant} from '../common/content/Text'

export const LegalFooter: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation('page')
  const {legal, trustpilot} = useFooterContent()
  const {trustpilotLinkHref} = useTrustpilotContent(TrustpilotWidgetVariant.reviews)

  return (
    <>
      <div className="wrapper">
        <ul className="list">
          {legal.map(({linkHref, linkText, trackedEvent}) => (
            <li key={linkHref} className="item">
              <Text variant={TextVariant.text14}>
                <Link href={linkHref} prefetch={false} legacyBehavior>
                  <a
                    className="link"
                    onClick={() => analytics.track(trackedEvent)}
                    role="link"
                    tabIndex={0}
                  >
                    {t(linkText)}
                  </a>
                </Link>
              </Text>
            </li>
          ))}
        </ul>
        <a
          className="trustpilot"
          href={trustpilotLinkHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => analytics.track(trustpilot.trackedEvent)}
        >
          <Text variant={TextVariant.text14} color={theme.palette.text.white}>
            <Trans
              i18nKey={trustpilot.description}
              components={{1: <span className="link">text</span>}}
            />
          </Text>
          <Image
            src={trustpilot.imageSrc}
            alt={trustpilot.imageAlt}
            width={82}
            height={20}
            style={{objectFit: 'contain', marginTop: `-${theme.spacing[0.5]}`}}
          />
        </a>
        <div className="dropdown">
          <DomainSettings />
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: ${theme.spacing[4]} 0;
        }

        .list {
          display: flex;
        }

        .item + .item {
          margin-left: ${theme.spacing[5]};
        }

        .link {
          color: ${theme.palette.text.white};
        }

        .link:hover,
        .link:focus {
          text-decoration: underline;
        }

        .trustpilot {
          display: flex;
          gap: ${theme.spacing[2]};
        }

        @media ${TABLET_SCREENSIZE} {
          .item + .item {
            margin-left: ${theme.spacing[4]};
          }
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
          }

          .list {
            flex-direction: column;
            align-items: center;
          }

          .item + .item {
            margin-left: 0;
            margin-top: ${theme.spacing[2]};
          }

          .dropdown {
            order: -1;
            margin-bottom: ${theme.spacing[4]};
          }

          .trustpilot {
            flex-direction: column;
            align-items: center;
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
